/**
 * ADR-0201 — the curation check's console surfaces, shared by all three trees.
 *
 * One module because the three editors (tenant knowledge, tenant guidance,
 * platform knowledge) show the same four things: a refusal card, a verdict
 * panel, a Verified column, and a Verify panel. Three copies of that would
 * drift the first time the wording changed.
 *
 * Served prose here never names a decision record, and never shows raw JSON:
 * every finding is rendered as a sentence a curator can act on.
 */

import { Link } from "react-router-dom";
import { formatTimestamp } from "./formatTime.js";

// ---------------------------------------------------------------------------
// Shapes — mirror the hub's, hand-written because the console has no shared types
// ---------------------------------------------------------------------------

export type CurationRelation = "duplicate" | "contradicts" | "complements" | "unrelated";
export type CurationAction = "write" | "merge_into" | "supersede" | "reject_duplicate";

/** Which band a finding came from — accepted canon, or the review queue. */
export type CurationScope = "accepted" | "pending";

export type CurationFinding = {
  name: string;
  relation: CurationRelation;
  why: string;
  score: number;
  tier: 1 | 2;
  /** Absent on checks stored before the queue was checked; reads as accepted. */
  scope?: CurationScope;
};

export type CurationRecommendation = {
  action: CurationAction;
  target?: string;
  merged_body?: string;
};

export type CurationCheck = {
  checked_at: string;
  tree_head: string;
  verdict: "full" | "deterministic_only" | "unavailable";
  model: string | null;
  findings: CurationFinding[];
  recommendation: CurationRecommendation | null;
  blocking: boolean;
};

/** Which entry-editing surface a card is rendered on — decides where a finding links. */
export type CurationTreeSurface = "knowledge" | "guidance" | "platform";

export const ENTRY_HREF: Record<CurationTreeSurface, (name: string) => string> = {
  knowledge: (name) => `/app/admin/tenant-knowledge/entry/${name}`,
  guidance: (name) => `/app/admin/tenant-guidance/entry/${name}`,
  platform: (name) => `/admin/platform-knowledge/entry/${name}`,
};

/**
 * Where a finding about a still-PENDING proposal links, or `null` when there is
 * nowhere honest to send the reader.
 *
 * Only tenant knowledge has a route per proposal. Guidance and platform review
 * from their list rows, and their tabs are localStorage-backed rather than
 * URL-backed, so there is no address that opens one of their proposals. A link
 * to the bare list would land on whichever tab that browser last used, which is
 * worse than no link: the reader follows it and has to find their way back.
 * Those two render the proposal's name as text instead.
 */
export const PROPOSAL_HREF: Record<CurationTreeSurface, (id: string) => string | null> = {
  knowledge: (id) => `/app/admin/tenant-knowledge/proposal/${id}`,
  guidance: () => null,
  platform: () => null,
};

// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------

/** A relation that refuses a curator's write unless overridden. */
export function isBlockingRelation(relation: CurationRelation): boolean {
  return relation === "duplicate" || relation === "contradicts";
}

/**
 * Whether a finding is one that can actually refuse a write.
 *
 * A clash with something still in the review queue is worth reading and never
 * refuses anything: nothing in the queue has been accepted, so it is not canon
 * to be measured against.
 */
export function isRefusingFinding(f: CurationFinding): boolean {
  return (f.scope ?? "accepted") === "accepted" && isBlockingRelation(f.relation);
}

/** Colour follows consequence: amber for the two that refuse a write, plain otherwise. */
export function relationPillClass(relation: CurationRelation): string {
  return isBlockingRelation(relation) ? "pill pillWarn" : "pill";
}

/** The pill a supersession relationship wears in a proposal's pill row. */
export function supersessionPillClass(role: "supersedes" | "superseded"): string {
  // The survivor is plain; the one on its way out is the one worth flagging.
  return role === "superseded" ? "pill pillWarn" : "pill";
}

/** The word a curator reads. Never the raw enum. */
export function relationLabel(relation: CurationRelation): string {
  switch (relation) {
    case "duplicate":
      return "duplicate";
    case "contradicts":
      return "contradicts";
    case "complements":
      return "complements";
    default:
      return "unrelated";
  }
}

/** One sentence naming what the check recommends. Null when it recommends nothing. */
export function recommendationSentence(rec: CurationRecommendation | null): string | null {
  if (!rec) return null;
  switch (rec.action) {
    case "merge_into":
      return rec.target ? `Fold this into ${rec.target}.` : "Fold this into an existing entry.";
    case "supersede":
      return rec.target ? `Replace ${rec.target} with this.` : "Replace an existing entry with this.";
    case "reject_duplicate":
      return rec.target ? `Nothing here that ${rec.target} does not already say.` : "Nothing here that is not already written down.";
    default:
      return "Write it as its own entry.";
  }
}

export const CHECK_HELP = {
  unavailable:
    "This is the duplicate scan on its own — no verdict was available to judge how the entries actually relate.",
  stale: "The knowledge base has changed since this was checked, so this verdict may be out of date.",
  never: "This was proposed before writes were checked.",
};

// ---------------------------------------------------------------------------
// Findings list — the one rendering, read-only
// ---------------------------------------------------------------------------

export function CurationFindings(props: {
  findings: CurationFinding[];
  surface: CurationTreeSurface;
  /** Show only the findings that refuse a write. */
  blockingOnly?: boolean;
}) {
  const rows = props.blockingOnly ? props.findings.filter(isRefusingFinding) : props.findings;
  if (rows.length === 0) return null;
  return (
    <ul className="curationFindings">
      {rows.map((f) => {
        const pending = f.scope === "pending";
        const href = pending ? PROPOSAL_HREF[props.surface](f.name) : ENTRY_HREF[props.surface](f.name);
        return (
          <li key={`${f.scope ?? "accepted"}:${f.name}`}>
            <span className={relationPillClass(f.relation)}>{relationLabel(f.relation)}</span>{" "}
            {href ? (
              <Link to={href} target="_blank" rel="noreferrer">
                {f.name}
              </Link>
            ) : (
              <span className="mono">{f.name}</span>
            )}
            {pending ? <span className="pill"> in review</span> : null}
            {f.why ? <span className="help"> — {f.why}</span> : null}
          </li>
        );
      })}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// The refusal card — shown in place, never as a toast (a toast cannot be acted on)
// ---------------------------------------------------------------------------

export function CurationCheckCard(props: {
  check: CurationCheck;
  surface: CurationTreeSurface;
  /**
   * What is being refused, in the reader's terms. A direct write is a save; an
   * accept is the entry itself landing in canon. Same card, same findings, same
   * override — the heading is the only thing that differs, so it is a prop
   * rather than a second component.
   */
  subject?: "save" | "entry";
  overrideReason: string;
  onOverrideReasonChange: (value: string) => void;
  onCancel: () => void;
  onOverride: () => void;
  busy?: boolean;
  /** Rendered above the check section when a second gate also refused this save. */
  extraSection?: React.ReactNode;
}) {
  const blocking = props.check.findings.filter(isRefusingFinding);
  const duplicates = blocking.filter((f) => f.relation === "duplicate").map((f) => f.name);
  const contradicts = blocking.filter((f) => f.relation === "contradicts").map((f) => f.name);
  const parts: string[] = [];
  if (duplicates.length > 0) parts.push(`would duplicate ${duplicates.join(", ")}`);
  if (contradicts.length > 0) parts.push(`would contradict ${contradicts.join(", ")}`);
  const subject = props.subject === "entry" ? "This entry" : "This save";

  return (
    <div className="card curationBlockCard">
      {props.extraSection}
      <div style={{ fontWeight: 600 }}>
        {subject} {parts.length > 0 ? parts.join(" and ") : "matches what is already accepted"}
      </div>
      <CurationFindings findings={props.check.findings} surface={props.surface} blockingOnly />
      {props.check.verdict === "unavailable" ? <div className="help">{CHECK_HELP.unavailable}</div> : null}
      <label className="field">
        <span className="label">
          {props.subject === "entry"
            ? "Reason to accept anyway (recorded in this entry's history)"
            : "Override reason (saves anyway, recorded in this entry's history)"}
        </span>
        <input
          className="input"
          value={props.overrideReason}
          onChange={(e) => props.onOverrideReasonChange(e.target.value)}
          placeholder="Why this belongs as its own entry"
          maxLength={500}
        />
      </label>
      <div className="actions">
        <button className="btn" onClick={props.onCancel}>
          Cancel
        </button>
        <button
          className="btn btnDanger"
          disabled={props.busy || !props.overrideReason.trim()}
          onClick={props.onOverride}
        >
          {props.subject === "entry" ? "Accept anyway" : "Override & save"}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The verdict panel — what a reviewer opens a proposal to
// ---------------------------------------------------------------------------

export function CurationVerdictPanel(props: {
  check: CurationCheck | null;
  surface: CurationTreeSurface;
  /** True when the tree has moved since the check ran. */
  stale?: boolean;
  onRecheck?: () => void;
  recheckBusy?: boolean;
  /** Offered only when the check drafted a merge. */
  onApplyMergeDraft?: () => void;
}) {
  if (!props.check) {
    return (
      <div className="card curationVerdictCard">
        <div style={{ fontWeight: 600 }}>Not checked</div>
        <div className="help">{CHECK_HELP.never}</div>
        {props.onRecheck ? (
          <div className="actions">
            <button className="btn btnSm" disabled={props.recheckBusy} onClick={props.onRecheck}>
              Check now
            </button>
          </div>
        ) : null}
      </div>
    );
  }

  const check = props.check;
  const sentence = recommendationSentence(check.recommendation);
  const canApplyDraft =
    check.recommendation?.action === "merge_into" &&
    Boolean(check.recommendation.merged_body) &&
    Boolean(props.onApplyMergeDraft);

  return (
    <div className="card curationVerdictCard">
      <div style={{ fontWeight: 600 }}>
        Checked {formatTimestamp(check.checked_at)} against the knowledge base as it was then
      </div>
      {sentence ? <div>Recommended: {sentence}</div> : null}
      {check.findings.length > 0 ? (
        <CurationFindings findings={check.findings} surface={props.surface} />
      ) : (
        <div className="help">Nothing in the knowledge base looked related.</div>
      )}
      {check.verdict === "unavailable" ? <div className="help">{CHECK_HELP.unavailable}</div> : null}
      {props.stale ? <div className="help">{CHECK_HELP.stale}</div> : null}
      {canApplyDraft || (props.stale && props.onRecheck) ? (
        <div className="actions">
          {canApplyDraft ? (
            <button className="btn btnSm" onClick={props.onApplyMergeDraft}>
              Apply merge draft
            </button>
          ) : null}
          {props.stale && props.onRecheck ? (
            <button className="btn btnSm" disabled={props.recheckBusy} onClick={props.onRecheck}>
              Check again
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Verification — the column and the line
// ---------------------------------------------------------------------------

export type VerificationFields = {
  verified_at?: string | null;
  verified_by_actor?: string | null;
  verify_every_days?: number | null;
  lapsed?: boolean;
};

export const VERIFY_HELP = {
  column: "When a curator last confirmed this entry still holds. A lapsed entry still serves — it just has not been looked at lately.",
  cadenceField: "Blank = 90 days. Set 30 for rates, prices, and anything that changes with the calendar.",
  lapsed: "Past its re-confirmation cadence. Still served, still read — nobody has confirmed it lately.",
};

/**
 * The Verified column cell.
 *
 * One datum per column: the date, muted when lapsed. No pill in this cell —
 * lapsed is a filter and a sort, not a second value crowding the date.
 */
export function verifiedCell(entry: VerificationFields) {
  if (!entry.verified_at) return <td className="muted">—</td>;
  return (
    <td className={entry.lapsed ? "muted" : undefined} title={entry.lapsed ? VERIFY_HELP.lapsed : undefined}>
      {formatTimestamp(entry.verified_at)}
    </td>
  );
}

/** "Verified <date> by <who>" / "Never verified" — the line on a detail page. */
export function verifiedLine(entry: VerificationFields): string {
  if (!entry.verified_at) return "Never verified";
  const who = entry.verified_by_actor ? ` by ${entry.verified_by_actor}` : "";
  return `Verified ${formatTimestamp(entry.verified_at)}${who}`;
}

/** Sort comparator: lapsed first, then oldest verification first. */
export function byVerificationUrgency(a: VerificationFields, b: VerificationFields): number {
  if (Boolean(a.lapsed) !== Boolean(b.lapsed)) return a.lapsed ? -1 : 1;
  const at = a.verified_at ?? "";
  const bt = b.verified_at ?? "";
  return at.localeCompare(bt);
}

/**
 * Validate the per-entry cadence field. Returns an error sentence, or null.
 * Called on blur — never while the curator is still typing a number.
 */
export function cadenceError(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null; // blank = the default
  const n = Number(trimmed);
  if (!Number.isFinite(n) || !Number.isInteger(n)) return "Enter a whole number of days.";
  if (n < 1 || n > 365) return "Enter between 1 and 365 days.";
  return null;
}

/** The cadence value to send: a number, or undefined for "use the default". */
export function cadencePayload(raw: string): number | undefined {
  const trimmed = raw.trim();
  if (!trimmed) return undefined;
  const n = Number(trimmed);
  return Number.isFinite(n) && Number.isInteger(n) && n >= 1 && n <= 365 ? n : undefined;
}
